import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyRadioChange as MatRadioChange } from '@angular/material/legacy-radio';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { Product } from '../../../products/interfaces';
import { ProductDataSource } from '../../../products/product-data-source';
import { ProductsService } from '../../../products/products.service';
import { ConnectAppsService } from '../../../profile/group-settings/group-settings-panel/connected-apps/services/connect-apps.service';
import { AuthService, AuthType } from '../../services/auth.service';

@Component({
  templateUrl: './topic-search-dialog.component.html',
  styleUrls: ['./topic-search-dialog.component.scss']
})
export class TopicSearchDialogComponent implements OnInit, OnDestroy {
  loading: boolean;
  dataSource: ProductDataSource;
  filteredItemsIds: string[];
  items: { id: string, name: string, slug?: string }[] = []
  searchedItems: { id: string, name: string, slug?: string }[] = []
  selectedItems: { id: string, name: string, slug?: string }[] = []
  selectedItem: { id: string, name: string, slug?: string };
  showAll: boolean = true;
  private _subs: SubSink = new SubSink();
  private searchDecouncer$: Subject<string> = new Subject();
  title: string;
  constructor(
    private productsService: ProductsService,
    private connectAppsService: ConnectAppsService,
    private authService: AuthService,
    private dialogRef: MatDialogRef<TopicSearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      accessList: string,
      selectedItemId: string,
      isRelatedTopicCall: boolean,
      isLeadLineCall: boolean,
    }
  ) {
    this.title = 'CustomTabsPage.SelectProduct';
    if (this.data?.isLeadLineCall) {
      this.fetchLeadLineProducts();
    } else {
      this.dataSource = new ProductDataSource(this.productsService)
      this.initTopics()
      this._subs.sink = this.dataSource.loading$
        .subscribe(isLoading => {
          if (isLoading) {
            this.loading = true;
          } else {
            this.loading = false;
            this.mapProductsToItems();
          }
        });
    }
  }

  ngOnInit(): void {
    this.setupSearchDebouncer();
  }

  loadSelectedItems() {
    if (this.data.selectedItemId) {
      this.selectedItems = this.data.selectedItemId.split(',').map(id => this.searchedItems.find(item => item.id == id)).filter(s => !!s)
    }
  }

  isRadioButtonDisabled(itemId) {
    if (this.data?.selectedItemId && this.data?.selectedItemId == itemId) {
      return true;
    }
    return false;
  }

  initTopics() {

    if (this.data?.accessList) {
      this.loadProducts(this.data.accessList)
    } else {
      this.loadProducts()
    }
    this.mapProductsToItems();
  }

  fetchLeadLineProducts() {
    this.loading = true;
    this.authService.getAddonsAuth(this.authService.idenediGroupId).pipe(finalize(() => {
      this.loading = false;
    }), map(addons => {
      return addons.find(add => add.Type == AuthType.LeadLine);
    }), switchMap(addon => {
      return this.connectAppsService.getLeadLineProducts(addon.BaseUrl, addon.PublicProperties?.customHeader);
    })).subscribe(res => {
      this.mapLeadLineProductsToItems(res);
      this.loadSelectedItems();
    })
  }

  loadProducts(organizationId?: string, searchTerm?: string) {
    this.dataSource.loadProducts(
      {
        limit: 20,
        start: 0,
        activeOnly: false,
        isAdminCall: false,
        organziationId: organizationId ?? null,
        searchTerm: searchTerm ?? null,
        topicTypeId: this.data?.isRelatedTopicCall ? this.data?.selectedItemId : null
      }, false, this.data?.isRelatedTopicCall,)
  }

  mapProductsToItems() {
    this.items = this.dataSource?.data?.map((d: Product) => { return { id: d.TopicId, name: d.TopicName, slug: d.Slug } }) ?? [];
    this.searchedItems = this.items;
  }

  mapLeadLineProductsToItems(products) {
    this.items = products.map((p) => { return { id: p.productId, name: p.productName } }) ?? [];
    this.searchedItems = this.items;
  }


  onScroll() {
    if (!this.loading && !this.data?.isLeadLineCall) {
      this.dataSource?.loadMore();
    }
  }

  isSelected(itemId): boolean {
    if (this.data?.isLeadLineCall) {
      return this.selectedItems.some(i => i.id == itemId);
    } else {
      if (this.selectedItem && !this.data.selectedItemId) {
        return this.selectedItem.id == itemId;
      } else if (this.data?.selectedItemId && this.data?.selectedItemId == itemId) {
        return true;
      }
      return false;
    }
  }

  onRadioBtnChange(event: MatRadioChange) {
    if (this.data?.isLeadLineCall) {
      const itemId = event.source.value;
      const item = this.searchedItems.find(i => i.id == itemId);
      this.selectedItems = [];
      this.selectedItems.push(item);
    } else {
      const itemId = event.source.value;
      const item = this.searchedItems.find(i => i.id == itemId);
      this.selectedItem = item;
    }
  }

  closeDialog() {
    if (this.data?.isLeadLineCall) {
      this.dialogRef.close(this.selectedItems);
    } else {
      this.dialogRef.close(this.selectedItem);
    }
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  onSearchInputChange(event: any) {
    this.searchDecouncer$.next(event)
  }

  private setupSearchDebouncer(): void {
    this.searchDecouncer$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe((term: string) => {
      this.search(term);
    });
  }

  private search(term: string): void {
    if (this.data?.isLeadLineCall) {
      this.searchLeadLineProducts(term);
    } else {
      if (term.length) {
        this.searchedItems = []
        this.loadProducts(this.data.accessList, term)
      }
      else {
        this.loadProducts(this.data.accessList, null)
      }
      this.mapProductsToItems();
    }
  }

  private searchLeadLineProducts(term: string) {
    this.searchedItems = this.items;
    if (term?.length) {
      this.searchedItems = this.searchedItems.filter(i => i.name.toLowerCase().includes(term.toLowerCase()));
    } else {
      this.fetchLeadLineProducts();
    }
  }
}
